import type { RecordingViewModel } from '@/apps/latest/shared/recording-status/types'

import type { NutritionReport, NutritionStage } from './api'

// 記録の画面の段階。録音 (共有カーネルの view-model) と、back が決めた記録の段 (report.stage。
// nutrition_counseling/pipeline.py の stage) を合わせる純関数。段は back の 1 か所で決まり、画面は組まない
// (処理中のまま止まった記録も back が失敗の段で返す。yakureki-back log/272 決定 13)。
//   loading:           報告書を取得中
//   before:            まだ録音していない (PENDING の器)。添付とメモが先に来る
//   recording:         マイクを握っている。送信に失敗して音声の控えを握っている間も含む (再送の導線)
//   transcribing:      音声を送っている 〜 文字起こしが済むまで
//   ready:             文字起こし済みで、まだ作っていない。「作る」(録り直しもできる)
//   generating:        報告書を作成中 (作り直しも同じ)
//   transcribeFailed:  文字起こしに失敗した (会話が無かった・処理中のまま止まった、も同じ)。
//                      ブラウザは控えを捨てているので「もう一度文字起こす」か、録り直す
//   generateFailed:    文字起こしは済んだが作れなかった (処理中のまま止まった、も同じ)。「作る」をもう一度
//   completed:         報告書が在る (作り直しに失敗しても前の報告書を出す。名前の脇の 1 行は regenerateFailed)
export type NutritionPhase =
  | 'loading'
  | 'before'
  | 'recording'
  | 'transcribing'
  | 'ready'
  | 'generating'
  | 'transcribeFailed'
  | 'generateFailed'
  | 'completed'

type RecordingInput = Pick<RecordingViewModel, 'status' | 'showRetryCta'>

const FROM_STAGE: Record<NutritionStage, NutritionPhase> = {
  before: 'before',
  transcribing: 'transcribing',
  ready: 'ready',
  generating: 'generating',
  transcribe_failed: 'transcribeFailed',
  generate_failed: 'generateFailed',
  completed: 'completed',
  regenerate_failed: 'completed',
}

export const deriveNutritionPhase = (
  recording: RecordingInput,
  report: Pick<NutritionReport, 'stage'> | undefined
): NutritionPhase => {
  const s = recording.status
  if (s === 'starting' || s === 'recording' || s === 'paused')
    return 'recording'
  if (s === 'failed' && recording.showRetryCta) return 'recording'
  if (s === 'stopping') return 'transcribing'
  if (report === undefined) return 'loading'
  return FROM_STAGE[report.stage]
}

// 処理中の段 (back の thread が段を進めに戻ってくる)。報告書を時々取り直し、止まった処理を back の判定で知る
export const isProcessingStage = (stage: NutritionStage | undefined) =>
  stage === 'transcribing' || stage === 'generating'

// 作る前の材料の欄 (添付とメモの横並び) を出す段階。録音中も文字起こし中も足せる。
// 作っている間 (generating・loading) はどこからも足せない。完了後は欄を出さず、回の 資料 のタブから足して
// 「作り直す」(design-book 04-apps/kakeru.md「管理栄養士の記録」・ADR-0085)。
export const acceptsMaterials = (phase: NutritionPhase): boolean =>
  phase === 'before' ||
  phase === 'recording' ||
  phase === 'transcribing' ||
  phase === 'ready' ||
  phase === 'transcribeFailed' ||
  phase === 'generateFailed'
